import React, { FC, useMemo } from 'react';
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { AbilityModel } from '../../models/AbilityModel';
import { PAYMENT_VOUCHER, RECIEPT_VOUCHER } from '../../constants/common';
import { formatPrice } from '../../selectors';
import SortableColumn from '../../components/Table/SortableColumn';
import { HeaderModel } from './Abilities';
import moment from 'moment';

type Props = {
    data: Array<AbilityModel>
    header: HeaderModel[],
    handleSort: (sortBy: string, direction: 'asc' | 'desc' | 'none') => void;
    openColConfig: () => void
    hideCols: string[]
};

const AbilitiesTable: FC<Props> = ({ data, header, handleSort, openColConfig, hideCols }) => {
    const intl = useIntl();
    const history = useHistory();
    const EXPENSES = [
        'expenses','tax','salary','s_insurance','h_insurance','u_insurance',
        'bank_fee',
        'personal_used'
    ];
    const INCOMES = [
        'sales',
        'bank_disburses_funds',
        'interest_on_deposit'
    ];

    const renderType = (item: AbilityModel) => {
        switch (true) {
            case INCOMES.includes(item.purpose ?? ''):
            case item.purpose === 'withdraw' && item.payment_method === 'cash':
            case item.purpose === 'deposit' && item.payment_method === 'banking':
                return 'Phiếu thu';
            case EXPENSES.includes(item.purpose ?? ''):
            case item.purpose === 'withdraw' && item.payment_method === 'banking':
            case item.purpose === 'deposit' && item.payment_method === 'cash':
                return 'Phiếu chi';
        }
    }

    const getDirection = (item: AbilityModel) => {
        switch (true) {
            case INCOMES.includes(item.purpose ?? ''):
            case item.purpose === 'withdraw' && item.payment_method === 'cash':
            case item.purpose === 'deposit' && item.payment_method === 'banking':
                return 'in';
            case EXPENSES.includes(item.purpose ?? ''):
            case item.purpose === 'withdraw' && item.payment_method === 'banking':
            case item.purpose === 'deposit' && item.payment_method === 'cash':
                return 'out';
        }
    }

    const renderIncomeType = (item: AbilityModel) => {
        switch (true) {
            case item?.purpose === 'sales':
                return 'Công nợ';
            case item?.purpose === 'bank_disburses_funds':
                return 'Ngân hàng giải ngân';
            case item?.purpose === 'interest_on_deposit':
                return 'Lãi tiền gửi';
            case item?.purpose === 'withdraw' && item?.payment_method === 'cash':
                return 'Rút tiền mặt';
            case item?.purpose === 'deposit' && item?.payment_method === 'banking':
                return 'Nộp tiền ngân hàng';
            case item?.expense_group === 'payroll' && item?.purpose === 'salary':
                return 'Chi phí lương';
            case item?.expense_group === 'payroll' && item?.purpose === 's_insurance':
                return 'Chi phí BHXH';
            case item?.expense_group === 'payroll' && item?.purpose === 'h_insurance':
                return 'Chi phí BHSK';
            case item?.expense_group === 'payroll' && item?.purpose === 'u_insurance':
                return 'Chi phí BHTN';
            case item?.purpose === 'personal_used':
                return 'Chi tiêu cá nhân';
            case item?.purpose === 'tax':
                return 'Thuế';
            case item?.expense_group === 'electric':
                return 'Chi phí điện';
            case item?.purpose === 'bank_fee':
                return 'Phí ngân hàng';
            case item?.expense_group === 'water':
                return 'Chi phí nước';
            case item?.expense_group === 'telecom':
                return 'Chi phí viễn thông';
            case item?.expense_group === 'rent':
                return 'Chi phí thuê';
            case item?.expense_group === 'manage':
                return 'Chi phí quản lý';
            case item?.expense_group === 'depreciation':
                return 'Chi phí khấu hao';
            case item?.expense_group === 'interest':
                return 'Chi phí lãi vay';
            case item?.expense_group === 'other':
                return 'Chi phí khác';
            case item?.purpose === 'withdraw' && item?.payment_method === 'banking':
                return 'Rút tiền mặt';
            case item?.purpose === 'deposit' && item?.payment_method === 'cash':
                return 'Nộp tiền ngân hàng';
            case item?.purpose === 'expenses' && (!item?.expense_group):
                return 'Chi công nợ NCC';
            default:
                return '';
        }
    };

    return (
        <div className='table-responsive'>
            <table className="table table-hover table-bordered table-striped border gy-7 gs-7">
                <thead>
                    <tr className="fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200">
                        <th>
                            <i 
                                className="fa fa-cogs"
                                style={{cursor: 'pointer'}}
                                onClick={openColConfig}
                            ></i>
                        </th>
                        {header.map((th, index) => {
                            if (!hideCols.includes(th.field)) {
                                if (th.sortable) {
                                    return (
                                        <SortableColumn
                                            key={'th-'+index}
                                            sortBy={th?.field}
                                            onSortChange={handleSort}
                                            style={th?.style ?? {}}
                                        >
                                            {th.label}
                                        </SortableColumn>
                                    )
                                }
                                return (
                                    <th style={th?.style ?? {}}>{th.label}</th>
                                )
                            }
                        })}
                    </tr>
                </thead>
                <tbody>
                    {data?.map((item: AbilityModel, index: number) => {
                        const direction = item.dh_payment?.direction ?? getDirection(item);
                        const invoices = item.order?.invoices ?? (item.receipt?.invoices ?? [])
                        const lastInvoice = invoices?.length ? invoices[invoices.length - 1] : null;
                        let invoiceStr = null;
                        if (lastInvoice) {
                            if (item.ref_order_id) {
                                invoiceStr = lastInvoice.invoice_template_code+lastInvoice.invoice_no
                            } else {
                                invoiceStr = lastInvoice.label
                            }
                        }

                        let note = item.note ?? '';
                        if (invoiceStr && !note.includes(invoiceStr)) {
                            note += ' | HĐ: ' + invoiceStr
                        }

                        return (
                            <tr key={`ability_${index}`} className="cursor-pointer">
                                <td></td>
                                {!hideCols.includes('type') && (
                                    <td>{renderType(item)}</td>
                                )}
                                {!hideCols.includes('created_at') && (
                                    <td>{moment(item.created_at).format('DD-MM-YYYY H:m:s')}</td>
                                )}
                                 {!hideCols.includes('date') && (
                                    <td>{item.date ?? ''}</td>
                                )}
                                {!hideCols.includes('name') && (
                                    <td>{item.name}</td>
                                )}
                                {!hideCols.includes('bank_id') && (
                                    <td className='text-center'>{item?.bank ? item.bank.bank_name + ' - ' + item.bank.bank_account_number : ''}</td>
                                )}
                                {!hideCols.includes('income') && (
                                    <td className='text-end'>{direction === 'in' ? formatPrice(item.dh_payment?.amount ?? item.value) : ''}</td>
                                )}
                                {!hideCols.includes('outcome') && (
                                    <td className='text-end'>{direction === 'out' ? formatPrice(item.dh_payment?.amount ?? item.value) : ''}</td>
                                )}
                                {!hideCols.includes('purpose') && (
                                    <td className='text-center'>{renderIncomeType(item)}</td>
                                )}
                                {!hideCols.includes('note') && (
                                    <td>{note}</td>
                                )}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default AbilitiesTable;